/// <amd-module name="WorkItemsHub/DataProviders/WorkItemsHubDataProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { WorkItemsHubRouteConstants, WorkItemsHubTabs } from "WorkItemsHub/Generated/Constants";
import { WorkItemsHubData, WorkItemsHubPagedData } from "WorkItemsHub/Generated/Contracts";
import * as WorkItemsHubTabUtils from "WorkItemsHub/Utils/WorkItemsHubTabUtils";

// new- data providers
export namespace WorkItemsHubDataProviderConstants {
    export const DefaultDataProvider = "ms.vss-work-web.new-work-items-hub-default-data-provider";
    export const AssignedToMeDataProvider = "ms.vss-work-web.new-work-items-hub-assignedtome-tab-data-provider";
    export const FollowingDataProvider = "ms.vss-work-web.new-work-items-hub-following-tab-data-provider";
    export const MentionedDataProvider = "ms.vss-work-web.new-work-items-hub-mentioned-tab-data-provider";
    export const MyActivityDataProvider = "ms.vss-work-web.new-work-items-hub-myactivity-tab-data-provider";
    export const RecentlyUpdatedDataProvider = "ms.vss-work-web.new-work-items-hub-recentlyupdated-tab-data-provider";
    export const RecentlyCreatedDataProvider = "ms.vss-work-web.new-work-items-hub-recentlycreated-tab-data-provider";
    export const RecentlyCompletedDataProvider = "ms.vss-work-web.new-work-items-hub-recentlycompleted-tab-data-provider";
    export const MyTeamsDataProvider = "ms.vss-work-web.new-work-items-hub-myteams-tab-data-provider";
}

const DataProviderByTabMap: { [key: number]: string } = {
    [WorkItemsHubTabs.AssignedToMe]: WorkItemsHubDataProviderConstants.AssignedToMeDataProvider,
    [WorkItemsHubTabs.Following]: WorkItemsHubDataProviderConstants.FollowingDataProvider,
    [WorkItemsHubTabs.Mentioned]: WorkItemsHubDataProviderConstants.MentionedDataProvider,
    [WorkItemsHubTabs.MyActivity]: WorkItemsHubDataProviderConstants.MyActivityDataProvider,
    [WorkItemsHubTabs.RecentlyUpdated]: WorkItemsHubDataProviderConstants.RecentlyUpdatedDataProvider,
    [WorkItemsHubTabs.RecentlyCreated]: WorkItemsHubDataProviderConstants.RecentlyCreatedDataProvider,
    [WorkItemsHubTabs.RecentlyCompleted]: WorkItemsHubDataProviderConstants.RecentlyCompletedDataProvider,
    [WorkItemsHubTabs.MyTeams]: WorkItemsHubDataProviderConstants.MyTeamsDataProvider
};

/**
 * Provides the raw hub data.
 */
export namespace WorkItemsHubDataProvider {
    export function refreshDataAsync(pageContext: IVssPageContext, tabId: string, pageSize: number): Promise<WorkItemsHubData> {
        const dataProviderId = getDataProviderId(tabId);
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<WorkItemsHubData>(dataProviderId, { pageSize }, true);
    }

    export function getData(pageContext: IVssPageContext, tabId: string): WorkItemsHubData {
        const dataProviderId = getDataProviderId(tabId);
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getData<WorkItemsHubData>(dataProviderId);
    }

    export function getDataForCurrentTab(pageContext: IVssPageContext): WorkItemsHubData {
        const currentTabId = getCurrentTabId(pageContext);
        return getData(pageContext, currentTabId);
    }

    export function getDataProviderId(tabId: string): string {
        const tab: WorkItemsHubTabs = WorkItemsHubTabUtils.TabEnumValueByTabIdMap[tabId];
        let dataProviderId = DataProviderByTabMap[tab];
        if (!dataProviderId) {
            dataProviderId = WorkItemsHubDataProviderConstants.DefaultDataProvider;
        }

        return dataProviderId;
    }

    export function getCurrentTabId(pageContext: IVssPageContext): string {
        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getCurrentRouteValues();
        const name = routeValues[WorkItemsHubRouteConstants.TabRouteParameterName];
        return WorkItemsHubTabUtils.getTabIdByName(name);
    }

    export function pageWorkItems(
        pageContext: IVssPageContext,
        tabId: string,
        workItemIds: number[],
        fieldReferenceNames: string[]
    ): Promise<WorkItemsHubPagedData> {
        const dataProviderId = getDataProviderId(tabId);
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<WorkItemsHubPagedData>(dataProviderId, { workItemIds, fieldReferenceNames }, true);
    }
}
